import { useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";

const SITE_URL = "https://footpoolevents.ch";
const PROD_HOSTS = ["footpoolevents.ch", "www.footpoolevents.ch"];

const setMeta = (selector, attr, value) => {
  if (typeof document === "undefined" || !value) return;
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

/**
 * Keeps <title>, meta description, OG/Twitter tags, canonical, html lang
 * in sync with the current UI language. All values come from the shared
 * translations bundle so FR/DE/IT stay consistent.
 */
export const SEO = () => {
  const { lang, t } = useLanguage();

  useEffect(() => {
    const seo = t.seo;
    if (!seo) return;

    // <html lang>
    document.documentElement.lang = seo.htmlLang || lang;

    // <title>
    document.title = seo.title;

    // Basic
    setMeta('meta[name="description"]', "content", seo.description);
    if (seo.keywords) setMeta('meta[name="keywords"]', "content", seo.keywords);

    // Open Graph
    setMeta('meta[property="og:title"]', "content", seo.ogTitle || seo.title);
    setMeta(
      'meta[property="og:description"]',
      "content",
      seo.ogDescription || seo.description
    );
    setMeta('meta[property="og:locale"]', "content", seo.ogLocale);

    // og:locale:alternate — always the two NON-active locales
    document.head
      .querySelectorAll('meta[property="og:locale:alternate"]')
      .forEach((el) => el.remove());
    const ALL_LOCALES = { fr: "fr_CH", de: "de_CH", it: "it_CH" };
    Object.entries(ALL_LOCALES)
      .filter(([code]) => code !== lang)
      .forEach(([, loc]) => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:locale:alternate");
        m.setAttribute("content", loc);
        document.head.appendChild(m);
      });

    setMeta('meta[property="og:image"]', "content", `${SITE_URL}/images/og-hero.jpg`);
    if (seo.ogImageAlt) {
      setMeta('meta[property="og:image:alt"]', "content", seo.ogImageAlt);
      setMeta('meta[name="twitter:image:alt"]', "content", seo.ogImageAlt);
    }

    // Twitter
    setMeta('meta[name="twitter:title"]', "content", seo.twitterTitle || seo.title);
    setMeta(
      'meta[name="twitter:description"]',
      "content",
      seo.twitterDescription || seo.description
    );
    setMeta('meta[name="twitter:image"]', "content", `${SITE_URL}/images/og-hero.jpg`);

    // Canonical (per language; German default lives at /)
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      "href",
      lang === "de" ? `${SITE_URL}/` : `${SITE_URL}/?lang=${lang}`
    );

    // Keep preview/staging hosts out of the index (production unaffected)
    if (!PROD_HOSTS.includes(window.location.hostname)) {
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
      setMeta('meta[name="googlebot"]', "content", "noindex, nofollow");
    }
  }, [lang, t]);

  return null;
};

export default SEO;
